import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  Badge,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useLocalization } from '../../../hooks/useLocalization';
import type { AppBarNotification } from './notifications.query';

dayjs.extend(relativeTime);

export type NotificationItemProp = {
  notification: AppBarNotification;
  isToday: boolean;
  useCustodianNotifications: boolean;
  onClose: () => void;
  onMarkRead: (notificationId: string, onCompleted: () => void) => void;
};

export const NotificationItem = ({
  notification,
  isToday,
  useCustodianNotifications,
  onClose,
  onMarkRead,
}: NotificationItemProp) => {
  const { translateBackendString } = useLocalization();
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        p: 1,

        '&:hover': {
          background: theme.color.surfaceContainerVariant,
        },

        borderRadius: theme.borderRadius.md,
      }}
    >
      <Stack useFlexGap direction='row'>
        <Badge
          invisible={!!notification.read}
          variant='dot'
          color='error'
          sx={{ ml: 1, mr: 2, mt: 1.5 }}
        ></Badge>
        <Box>
          <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
            {translateBackendString(notification.title)}
          </Typography>
          <Typography variant='bodyMedium' sx={{ fontWeight: 400 }}>
            {translateBackendString(notification.body)}
          </Typography>
          <Typography
            variant='bodySmall'
            sx={{ color: theme.color.onSurfaceVariant }}
          >
            {isToday
              ? dayjs(notification.createdAt).fromNow()
              : dayjs(notification.createdAt).format('h:mm a')}
          </Typography>
          <Box
            sx={{
              mt: 0.5,
            }}
          >
            <Button
              size='small'
              variant='outlined'
              color='secondary'
              onClick={() => {
                if (!useCustodianNotifications) {
                  if (!notification.id) return;
                  onMarkRead(notification.id, () => {
                    if (notification.link) {
                      window.location.href = notification.link;
                    }
                  });
                  onClose();
                } else if (notification.link) {
                  window.location.href = `${window.location.origin}${notification.link}`;
                }
              }}
            >
              {translateBackendString(notification.linkText)}
            </Button>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};
